import { fabric } from 'fabric';
import { type Transform } from 'fabric/fabric-impl';
import type Table from '../objects/Table';
import ControlBase from '../fabric/ControlBase';
import { resizeWithSmartGuides } from '../objects/SmartGuideBehaviors';

const isRowControl = (control: 'mt' | 'mb' | 'ml' | 'mr') => {
  return control === 'mt' || control === 'mb';
};

const shouldResize = (corner, x, y, object, isRow = true) => {
  const { left, top } = object;
  const fromTopLeft =
    corner === 'mt' ||
    corner === 'ml' ||
    corner === 'tl' ||
    (corner === 'tr' && isRow) ||
    (corner === 'bl' && !isRow);
  if (fromTopLeft) {
    return isRow ? y < top : x < left;
  }
  return isRow ? y > top : x > left;
};

// Table scale never changes. Instead, we change the width/height and distribute
// that change across the table vectors
class TableLinearResizeControl extends ControlBase {
  // Name of the action the control is used for.
  actionName: string;
  edge: 'top' | 'bottom' | 'left' | 'right';
  isRow: boolean;

  constructor(options) {
    super(options);

    let axis;
    switch (this.edge) {
      case 'top':
        this.x = 0;
        this.y = -0.5;
        axis = 'h';
        break;
      case 'bottom':
        this.x = 0;
        this.y = 0.5;
        axis = 'h';
        break;
      case 'left':
        this.x = -0.5;
        this.y = 0;
        axis = 'v';
        break;
      case 'right':
        this.x = 0.5;
        this.y = 0;
        axis = 'v';
        break;
    }
    this.actionHandler = resizeWithSmartGuides(
      fabric.controlsUtils.wrapWithFixedAnchor(this.handler),
      axis
    );
    this.cursorStyle =
      this.edge === 'top' || this.edge === 'bottom' ? 'ns-resize' : 'ew-resize';
  }

  getMouseDownHandler() {
    return (eventData, transform) => {
      const object = transform.target;
      object.clearSelectedCells();
      object.toggleResizing(true);
      return true;
    };
  }

  getMouseUpHandler(): fabric.ControlMouseEventHandler {
    return (e: MouseEvent, transform: Transform) => {
      const object = transform.target as Table;
      object.toggleResizing(false);
      object.canvas.controller.undoManager.completeUndoGroup();

      return true;
    };
  }

  positionHandler(dim, finalMatrix, fabricObject, currentControl) {
    // hook into the position handler to update this controls sizeX and sizeY to cover an
    // entire edge of the bounding box minus a small margin from the corner controls.
    // .sizeX and .sizeY are used to determine the clickable area
    const thickness = fabricObject.cornerSize;
    const touchThickness = fabricObject.touchCornerSize;
    const margin = fabricObject.touchCornerSize;
    const zoom = fabricObject.canvas?.getZoom() || 1;
    if (this.edge === 'left' || this.edge === 'right') {
      this.sizeY = fabricObject.height * fabricObject.scaleY * zoom - margin;
      this.sizeX = thickness;
      this.touchSizeY = this.sizeY;
      this.touchSizeX = touchThickness;
    } else if (this.edge === 'top' || this.edge === 'bottom') {
      this.sizeY = thickness;
      this.sizeX = fabricObject.width * fabricObject.scaleX * zoom - margin;
      this.touchSizeY = touchThickness;
      this.touchSizeX = this.sizeX;
    }
    return super.positionHandler(
      dim,
      finalMatrix,
      fabricObject,
      currentControl
    );
  }

  handler(eventData, transform, x, y) {
    const object = transform.target;
    const isRow = isRowControl(transform.corner);
    const objDim = isRow ? object.height : object.width;
    const localPoint = fabric.controlsUtils.getLocalPoint(
      transform,
      transform.originX,
      transform.originY,
      x,
      y
    );
    const strokePadding = object.strokeWidth;
    const newDimension = isRow
      ? Math.abs(localPoint.y) - strokePadding
      : Math.abs(localPoint.x) - strokePadding;
    if (
      newDimension > objDim &&
      !shouldResize(transform.corner, x, y, object, isRow)
    ) {
      return false;
    }
    return object.handleResize(newDimension, isRow, transform.corner);
  }

  render() {
    // render(ctx: CanvasRenderingContext2D, left: number, top: number) {
    //   // Debug only
    //   ctx.save();
    //   ctx.lineJoin = 'round';
    //   ctx.fillStyle = 'blue';
    //   ctx.strokeStyle = '#999999';
    //   ctx.lineWidth = 3;
    //   ctx.fillRect(left, top, 10 + 10 * this.isRow, 10 + 10 * !this.isRow);
    //   ctx.restore();
  }
}
export default TableLinearResizeControl;
